import { makeAutoObservable, runInAction } from 'mobx'
import { isCancel } from 'axios'
import { RequestCancelAxios } from 'shared/api/lib'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import type {
  IMessagingChartResponse,
  IMessagingStatisticResponse,
  AnalyticsResponse,
  MessagingAnalyticsKeys,
  IPerformanceStatisticResponse,
  IPerformanceChartResponse,
  PerformanceAnalyticsKeys,
  ICallsStatisticResponse,
  ICallsChartResponse,
  CallsAnalyticsKeys,
} from 'entities/Analytics/api/types'
import { MessagesAnalyticsApi } from 'entities/Analytics/api/messagesAnalytics'
import { PerformanceAnalyticsApi } from 'entities/Analytics/api/performanceAnalytics'
import { CallsAnalyticsApi } from 'entities/Analytics'
import { AnalyticsFiltersStore } from 'pages/analytics/store/analyticsFiltersStore'

export class OverallAnalyticsPageStore {
  private readonly _analyticsFiltersStore: AnalyticsFiltersStore
  private readonly _requestCancelAxios = new RequestCancelAxios()

  private _error: string | null = null
  private _pageLoading = false
  private _performanceData: AnalyticsResponse<
    IPerformanceStatisticResponse,
    IPerformanceChartResponse
  > | null = null
  private _messagesData: AnalyticsResponse<
    IMessagingStatisticResponse,
    IMessagingChartResponse
  > | null = null
  private _callsData: AnalyticsResponse<ICallsStatisticResponse, ICallsChartResponse> | null = null

  constructor(entities: IEntitiesStore) {
    this._analyticsFiltersStore = new AnalyticsFiltersStore(entities, {
      onFiltersChange: (loadingType) => this._fetchAnalytics(loadingType),
      additionalFilterTypes: [],
      breakdownFilterTypes: [],
    })

    makeAutoObservable(this)
  }

  init = () => {
    this._analyticsFiltersStore.initReactions()
    this._fetchAnalytics('page')
  }

  private _fetchAnalytics = async (loadingType: 'page' | 'chart' = 'page') => {
    if (loadingType === 'page') {
      this._pageLoading = true
    }

    try {
      runInAction(() => {
        this._error = null
      })
      const { analyticsFiltersParams } = this._analyticsFiltersStore

      this._requestCancelAxios.init()

      const [performanceResponse, messagesResponse, callsResponse] = await Promise.all([
        PerformanceAnalyticsApi.getAnalytics(
          { ...analyticsFiltersParams, interval: 'week' },
          {
            cancelToken: this._requestCancelAxios.token,
          }
        ),
        MessagesAnalyticsApi.getAnalytics(
          { ...analyticsFiltersParams, interval: 'week' },
          {
            cancelToken: this._requestCancelAxios.token,
          }
        ),
        CallsAnalyticsApi.getAnalytics(
          { ...analyticsFiltersParams, interval: 'week' },
          {
            cancelToken: this._requestCancelAxios.token,
          }
        ),
      ])

      this._performanceData = performanceResponse.data
      this._messagesData = messagesResponse.data
      this._callsData = callsResponse.data
    } catch (error) {
      if (!isCancel(error)) {
        console.error('Overall analytics fetch error:', error)
        this._error = 'Failed to load analytics data'
      }
    } finally {
      if (loadingType === 'page') {
        this._pageLoading = false
      }
    }
  }

  getPerformanceMetricByKey = (key: PerformanceAnalyticsKeys) => {
    return this._performanceData?.statistic.find((metric) => metric.key === key)
  }

  getPerformanceChartByKey = (key: PerformanceAnalyticsKeys) => {
    return this._performanceData?.charts.map((chart) => chart[key])
  }

  getMessageMetricByKey = (key: MessagingAnalyticsKeys) => {
    return this._messagesData?.statistic.find((metric) => metric.key === key)
  }

  getMessageChartByKey = (key: MessagingAnalyticsKeys) => {
    return this._messagesData?.charts.map((chart) => chart[key])
  }

  getCallMetricByKey = (key: CallsAnalyticsKeys) => {
    return this._callsData?.statistic.find((metric) => metric.key === key)
  }

  getCallChartByKey = (key: CallsAnalyticsKeys) => {
    return this._callsData?.charts.map((chart) => chart[key])
  }

  get calendarFilterStore() {
    return this._analyticsFiltersStore.calendarFilterStore
  }

  get pageLoading() {
    return this._pageLoading
  }

  get error() {
    return this._error
  }

  get hasData() {
    return !!(this._performanceData || this._messagesData || this._callsData)
  }

  dispose = () => {
    this._analyticsFiltersStore.dispose()
    this._requestCancelAxios.reset()
  }
}
